/**
 * Utility functions for custom theme form
 * Handles color detection, field type determination, and nested value updates
 */

/**
 * Detect if a value is a color (hex or rgba/rgb)
 */
export const isColorValue = (value: any): boolean => {
  if (typeof value !== "string") return false;
  return isHexColor(value) || isRgbaColor(value);
};

/**
 * Detect if a string is a hex color
 */
export const isHexColor = (value: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
};

/**
 * Detect if a string is an rgba or rgb color
 */
export const isRgbaColor = (value: string): boolean => {
  return /^rgba?\(/.test(value);
};

/**
 * Calculate step value for number inputs based on decimal places
 */
export const calculateNumberStep = (value: number): number => {
  const decimalPlaces = (value.toString().split(".")[1] || "").length;
  return Math.pow(10, -decimalPlaces);
};

/**
 * Generate field label from path array
 */
export const getFieldPath = (path: string[]): string => {
  return path.join(".");
};

/**
 * Update a nested value in an object using a path array
 */
export const updateNestedValue = (
  obj: any,
  path: string[],
  value: any
): any => {
  if (path.length === 0) return value;

  const [key, ...rest] = path;

  if (rest.length === 0) {
    return { ...obj, [key]: value };
  }

  return {
    ...obj,
    [key]: updateNestedValue(obj[key] || {}, rest, value),
  };
};

/**
 * Get nested value from an object using a path array
 */
export const getNestedValue = (obj: any, path: string[]): any => {
  return path.reduce((current, key) => current?.[key], obj);
};

/**
 * Determine field type based on value
 */
export const getFieldType = (value: any): "color" | "number" | "string" => {
  if (typeof value === "number") return "number";
  if (isColorValue(value)) return "color";
  return "string";
};

/**
 * Validate color value and return normalized format
 */
export const normalizeColorValue = (value: string): string => {
  if (isHexColor(value)) {
    // Ensure hex colors are in 6-digit format and uppercase
    if (value.length === 4) {
      return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`.toUpperCase();
    }
    return value.toUpperCase();
  }

  if (isRgbaColor(value)) {
    return value;
  }

  // If it's not a valid color, return as-is (will be handled by validation)
  return value;
};

/**
 * Generate a unique key for form fields
 */
export const generateFieldKey = (path: string[]): string => {
  return path.join("-");
};

/**
 * Check if a value is a valid color for preview
 */
export const isValidColorForPreview = (value: string): boolean => {
  if (!isColorValue(value)) return false;

  // Additional validation for rgba colors
  if (isRgbaColor(value)) {
    const rgbaMatch = value.match(
      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
    );
    if (!rgbaMatch) return false;

    const [, r, g, b, a] = rgbaMatch;
    const numR = parseInt(r, 10);
    const numG = parseInt(g, 10);
    const numB = parseInt(b, 10);
    const numA = a ? parseFloat(a) : 1;

    return (
      numR >= 0 &&
      numR <= 255 &&
      numG >= 0 &&
      numG <= 255 &&
      numB >= 0 &&
      numB <= 255 &&
      numA >= 0 &&
      numA <= 1
    );
  }

  return true;
};
